import { forwardRef, useState } from 'react'

import { currencyToNumber, numberToCurrency } from '@/utils'
import { TextField } from './TextField'

const ALLOWED_INPUT_REGEX = /[^\d.\-,]/g

interface CurrencyFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string
  error?: string | null
  helpText?: string
  inputClassName?: string
  onChange?: (value: number) => void
}

const CurrencyField = forwardRef<HTMLInputElement, CurrencyFieldProps>(
  ({ onChange, value, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string | number>(() =>
      numberToCurrency(value as string | number | undefined),
    )

    const handleChange = (e: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(ALLOWED_INPUT_REGEX, '')
      setDisplayValue(inputValue)
    }

    const handleBlur = () => {
      const value = parseFloat(
        displayValue === '' ? '0' : `${displayValue}`.replace(/,/g, ''),
      )
      const currencyValue = numberToCurrency(displayValue)
      onChange?.(value)
      setDisplayValue(currencyValue)
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      const value = currencyToNumber(displayValue)
      setDisplayValue(value || '')
      setTimeout(() => e.target.select(), 0)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleBlur()
    }

    return (
      <TextField
        placeholder="$0.00"
        {...props}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        ref={ref}
      />
    )
  },
)

CurrencyField.displayName = 'CurrencyField'

export { CurrencyField }
